import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Header from '../../Components/Header/Header';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';
import { AppDispatch, RootState } from '../../Redux/Store';
import {
  createStaffReservation,
  fetchInquiriesForStaff,
  fetchReservationMenusForStaff,
  fetchStaffAvailability,
} from '../../Redux/Reservation/reservationApi';
import { fetchReservationSetting } from '../../Redux/ReservationSettings/reservationSettingsApi';
import { toDateString } from '../../types/reservation';
import {
  EMAIL_FORMAT_ERROR,
  PHONE_FORMAT_ERROR,
  isValidEmail,
  isValidPhoneNumber,
  normalizeEmail,
  normalizePhoneNumber,
} from '../../utils/contactValidation';

/** BE のエラーレスポンス（JSON {"message": "..."}）から表示用メッセージを取り出す */
const parseErrorMessage = (raw: string): string => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.message === 'string') return parsed.message;
  } catch {
    // JSON でなければそのまま表示
  }
  return raw;
};

/**
 * スタッフによる予約の代理登録（電話予約など）。
 * SlotPicker は availability を参照するが、スタッフは LENIENT 運用のため
 * 満枠の枠も選択可能とし、force 警告フローで強行登録できる。
 */
const ReservationNew: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { availability, availabilityLoading, menus, inquiries } = useSelector(
    (state: RootState) => state.reservation,
  );
  // コース必須設定（フィードバック4）。登録コースがある場合のみ必須化する
  const courseSelectionRequired = useSelector(
    (state: RootState) => state.reservationSettings.setting?.courseSelectionRequired === true,
  );

  const [date, setDate] = useState<string>(toDateString(new Date()));
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [partySize, setPartySize] = useState(2);
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [menuId, setMenuId] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);
  // 連絡先の形式エラー（blur 時+送信時に検証・項目直下に表示。フィードバック3）
  const [contactErrors, setContactErrors] = useState<{ email?: string; phone?: string }>({});

  useEffect(() => {
    if (store_id) {
      dispatch(fetchReservationMenusForStaff(store_id));
      dispatch(fetchInquiriesForStaff(store_id));
      // コース必須設定の判定に受付設定を取得する（フィードバック4）
      dispatch(fetchReservationSetting(store_id));
    }
  }, [dispatch, store_id]);

  // 日付・人数が変わったらその日の空き枠を取得（人数指定で「その人数で入れる枠」を正確に判定）
  useEffect(() => {
    if (store_id && date) {
      setSelectedSlot(null);
      dispatch(fetchStaffAvailability({ storeId: store_id, from: date, to: date, partySize }));
    }
  }, [dispatch, store_id, date, partySize]);

  const sortedInquiries = useMemo(
    () => [...inquiries].sort((a, b) => a.sortIndex - b.sortIndex),
    [inquiries],
  );

  const selectedSlotInfo = availability.find((s) => s.dateTime === selectedSlot);

  // コース必須（登録コースが1件以上ある場合のみ有効）かつ未選択か（フィードバック4）
  const courseRequiredUnmet =
    courseSelectionRequired && menus.length > 0 && !menuId;

  /**
   * blur 時+送信時の連絡先形式チェック（フィードバック3）。
   * 入力がある場合のみ検証し、エラー文言を項目直下に表示する。
   */
  const validateContact = (field: 'email' | 'phone'): boolean => {
    const value = (field === 'email' ? email : phone).trim();
    const invalid =
      value !== '' && !(field === 'email' ? isValidEmail(value) : isValidPhoneNumber(value));
    setContactErrors((prev) => {
      const next = { ...prev };
      if (invalid) next[field] = field === 'email' ? EMAIL_FORMAT_ERROR : PHONE_FORMAT_ERROR;
      else delete next[field];
      return next;
    });
    return !invalid;
  };

  const doSubmit = (force: boolean) => {
    if (!store_id || !selectedSlot) return;
    setSubmitting(true);
    setSubmitError(null);
    dispatch(
      createStaffReservation({
        storeId: store_id,
        input: {
          userName: userName.trim(),
          // 送信前に正規化（全角→半角・ハイフン/スペース除去）した値を送る
          email: email.trim() ? normalizeEmail(email) : null,
          phone: phone.trim() ? normalizePhoneNumber(phone) : null,
          reservationTime: selectedSlot,
          numberOfPeople: partySize,
          selectedMenuId: menuId || null,
          responses: Object.entries(answers)
            .filter(([, content]) => content !== '')
            .map(([inquiryId, content]) => ({ inquiryId, content })),
        },
        force,
      }),
    )
      .unwrap()
      .then((result) => {
        navigate(`/${store_id}/manage/reservation/${result.reservationId}`);
      })
      .catch((e: string) => setSubmitError(parseErrorMessage(e)))
      .finally(() => setSubmitting(false));
  };

  const handleSubmitClick = () => {
    // 送信時にも連絡先の形式を検証する（&& の短絡を避けるため個別に評価する）
    const emailOk = validateContact('email');
    const phoneOk = validateContact('phone');
    if (!emailOk || !phoneOk) return;
    // 満枠（bookable=false）の枠、またはコース必須なのに未選択の場合は
    // force 警告フローに入る（電話予約の柔軟対応のため強制登録は可能・フィードバック4）
    if ((selectedSlotInfo && !selectedSlotInfo.bookable) || courseRequiredUnmet) {
      setForceConfirmOpen(true);
    } else {
      doSubmit(false);
    }
  };

  const valid =
    userName.trim() !== '' &&
    selectedSlot !== null &&
    partySize >= 1 &&
    // 連絡先はメールまたは電話のいずれか必須（BE 制約）
    (email.trim() !== '' || phone.trim() !== '');

  return (
    <>
      <Header />
      <Container maxWidth="sm" sx={{ mt: 3, mb: 6 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/${store_id}/manage/reservation`)}
          sx={{ mb: 1 }}
        >
          予約管理へ戻る
        </Button>
        <Typography variant="h5" gutterBottom>
          予約の代理登録
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          電話などで受けた予約をスタッフが登録します。満枠の時間でも警告を確認のうえ登録できます。
        </Typography>

        {submitError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {submitError}
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                color="warning"
                variant="contained"
                disabled={submitting}
                onClick={() => setForceConfirmOpen(true)}
              >
                警告を無視して登録する（強制）
              </Button>
            </Box>
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            日時と人数
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              type="date"
              label="日付"
              size="small"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="party-size-label">人数</InputLabel>
              <Select
                labelId="party-size-label"
                label="人数"
                value={partySize}
                onChange={(e) => setPartySize(Number(e.target.value))}
              >
                {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}名
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {availabilityLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : availability.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              この日に予約枠がありません（休業日または営業時間未設定）
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {availability.map((slot) => {
                const time = slot.dateTime.substring(11, 16);
                const selected = selectedSlot === slot.dateTime;
                // 枠不可の理由を出し分ける（Wave A・4）。AFTER_HOURS は満席/満枠表現を使わない。
                const afterHours = !slot.bookable && slot.unavailableReason === 'AFTER_HOURS';
                const label = slot.bookable
                  ? time
                  : afterHours
                    ? `${time}（時間外）`
                    : `${time}（満枠）`;
                return (
                  <Chip
                    key={slot.dateTime}
                    label={label}
                    color={selected ? 'primary' : slot.bookable ? 'default' : 'warning'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => setSelectedSlot(slot.dateTime)}
                  />
                );
              })}
            </Box>
          )}
          {selectedSlotInfo && !selectedSlotInfo.bookable && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {selectedSlotInfo.unavailableReason === 'AFTER_HOURS'
                ? 'この枠はコース/デフォルト滞在時間が営業時間を超えます。登録する場合は強制登録になります。'
                : 'この枠は満枠です。登録する場合は強制登録（オーバーブック）になります。'}
            </Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            お客様情報
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="お名前"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              required
              size="small"
            />
            <TextField
              label="メールアドレス"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => validateContact('email')}
              error={!!contactErrors.email}
              size="small"
              helperText={contactErrors.email || '入力すると予約確認メールが送信されます'}
            />
            <TextField
              label="電話番号"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => validateContact('phone')}
              error={!!contactErrors.phone}
              size="small"
              helperText={contactErrors.phone || 'メールまたは電話番号のいずれかは必須です'}
            />
            {menus.length > 0 && (
              <FormControl size="small" fullWidth required={courseSelectionRequired}>
                <InputLabel id="menu-label">コース</InputLabel>
                <Select
                  labelId="menu-label"
                  label="コース"
                  value={menuId}
                  onChange={(e) => setMenuId(e.target.value)}
                >
                  <MenuItem value="">なし（席のみ）</MenuItem>
                  {menus.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}（税抜{m.price.toLocaleString()}円）
                    </MenuItem>
                  ))}
                </Select>
                {courseRequiredUnmet && (
                  <FormHelperText>
                    この店舗はコース選択が必須です（電話予約などで席のみ登録する場合は確認のうえ強制登録します）
                  </FormHelperText>
                )}
              </FormControl>
            )}
            {sortedInquiries.map((inq) =>
              inq.type === 'CHOICE' ? (
                <FormControl key={inq.id} size="small" fullWidth>
                  <InputLabel id={`inq-${inq.id}`}>{inq.content}</InputLabel>
                  <Select
                    labelId={`inq-${inq.id}`}
                    label={inq.content}
                    value={answers[inq.id] ?? ''}
                    onChange={(e) => setAnswers({ ...answers, [inq.id]: e.target.value })}
                  >
                    <MenuItem value="">未回答</MenuItem>
                    {inq.choices.map((c) => (
                      <MenuItem key={c.id} value={c.label}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  key={inq.id}
                  size="small"
                  label={inq.content}
                  value={answers[inq.id] ?? ''}
                  onChange={(e) => setAnswers({ ...answers, [inq.id]: e.target.value })}
                />
              ),
            )}
          </Box>
        </Paper>

        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!valid || submitting}
          onClick={handleSubmitClick}
        >
          {submitting ? '登録中...' : 'この内容で予約を登録'}
        </Button>
      </Container>

      {/* 満枠・警告時の強制登録確認 */}
      <ConfirmDialog
        open={forceConfirmOpen}
        onClose={() => setForceConfirmOpen(false)}
        onConfirm={() => {
          setForceConfirmOpen(false);
          doSubmit(true);
        }}
        title="強制登録の確認"
        message={
          courseRequiredUnmet
            ? 'この店舗はコース選択が必須ですが、コース未選択のまま予約を登録します（電話予約などの柔軟対応）。よろしいですか？'
            : '空き枠の条件を満たしていませんが、警告を無視して予約を登録します（席が割り当てられない場合は台帳の未割当レーンに表示されます）。よろしいですか？'
        }
        confirmText="強制登録する"
        cancelText="戻る"
        confirmColor="warning"
        closeColor="inherit"
      />
    </>
  );
};

export default ReservationNew;
